// consolidated slice actions for request, response, and query parameter schema editors

import type { APIDesignerStore } from '..';
import type { WritableDraft } from 'immer/dist/internal.js';
import type { ResponseObject, HttpMethods, MediaTypeObject } from 'oas/types';
import type { OpenAPIV3 } from 'openapi-types';
import type { StateCreator } from 'zustand';

import { codes } from '@readme/http-status-codes';

import { actionLog } from '@core/store/util';

import {
  MIMEType,
  type MIMETypeValue,
  type UpdateSchemaAttrObject,
  traverseAndModifyRequestSchema,
  traverseAndModifyResponseSchema,
  traverseAndModifyTextPlainResponseSchema,
  generateUniquePropertyName,
  isStatusCode,
  isPropertyPath,
  type SchemaEditorTarget,
  traverseAndModifyQueryParam,
  type ValidResponseSchema,
} from './utils';

export interface BodySchemaEditorSliceActions {
  /**
   * Adds a new property to the request or response body schema of the current endpoint.
   *
   * The first element is always 0, which represents the top-level object.
   * If the id is "0", it means the property should be added to root level of the schema object.
   * If the id is "0-1-2", it means the property should be added to the third property of the second property of the root schema object.
   */
  addProperty: (args: {
    /**
     * The type of the property to add - 'request' or 'response' or a 'query' object with its location info
     */
    target: SchemaEditorTarget;
    /**
     * The id of the property to add. This is a string that represents the path to the property in the schema.
     */
    id: string;
    /**
     * The HTTP status code of the response. Only used for response bodies.
     */
    statusCode?: string;
    /**
     * The MIME type of the property to add. Only used for response bodies.
     */
    mimeType?: MIMETypeValue;
  }) => void;

  /**
   * Adds a new response schema to the current endpoint.
   */
  addResponseSchema: (
    /**
     * The HTTP status code of the response
     */
    statusCode: string,
    /**
     * The MIME type of the response schema to add. Defaults to 'application/json'.
     */
    mimeType?: MIMETypeValue,
  ) => void;

  /**
   * Changes the media type of a response schema.
   */
  changeResponseMediaType: (
    /**
     * The HTTP status code of the response
     */
    statusCode: string,
    /**
     * The new MIME type of the response schema
     */
    newMimeType: MIMETypeValue,
    /**
     * The old MIME type of the response schema
     */
    oldMimeType?: MIMETypeValue,
  ) => void;

  /**
   * Changes the status code of a response schema.
   */
  changeResponseStatusCode: (
    /**
     * The HTTP status code of the response
     */
    statusCode: string,
    /**
     * The new HTTP status code of the response
     */
    newStatusCode: string,
  ) => void;

  /**
   * Gets all the status codes of response schemas for the current endpoint.
   *
   * @returns An array of status codes.
   */
  getAllResponseStatusCodes: () => string[];

  /**
   * Gets the status codes of response schemas that have multiple MIME types.
   *
   * @returns An array of status codes that have multiple MIME types.
   */
  getErroredResponseSchemas: () => string[];

  /**
   * Gets the existing media types for a response schema.
   *
   * @returns An array of media types.
   */
  getExistingMediaTypes: (
    /**
     * The HTTP status code of the response
     */
    statusCode: string,
  ) => MIMETypeValue[];

  /**
   * Gets the valid response schemas for the current endpoint with proper filtering and formatting.
   * By default, it looks for 'application/json' MIME types, but in the case where it doesn't find any, it will
   * return the first schema it finds, regardless of MIME type.
   *
   * Can be toggled to allow returning multiple schemas with different MIME types.
   *
   * If there is no schema AT ALL, it will return an empty array.
   * We should never modify the return value and must always stay true to the original schema/OAS file.
   * Use that OAS file as a single source of truth.
   *
   * @returns An array of response schemas with their status codes, descriptions, and MIME type schemas.
   */
  getValidResponseSchemas: () => ValidResponseSchema[];

  /**
   * Initializes an empty request or response body for the current endpoint with the specified status code
   * Note: This function only works with request and response bodies, not query parameters.
   */
  init: (
    /**
     * The target of the body to initialize - 'request' or 'response'
     */
    target: 'request' | 'response',
    /**
     * The HTTP status code of the response. Only used for response bodies.
     */
    statusCode?: string,
  ) => void;

  /**
   * Removes a property from the request body, response body, or query parameter schema of the current endpoint.
   */
  removeProperty: (args: {
    /**
     * The type of the property to remove - 'request' or 'response' or a 'query' object with its location info
     */
    target: SchemaEditorTarget;
    /**
     * The id of the property to remove. This is a string that represents the path to the property in the schema.
     */
    id: string;
    /**
     * The HTTP status code of the response. Only used for response bodies.
     */
    statusCode?: string;
    /**
     * The MIME type of the property to remove. Only used for response bodies.
     */
    mimeType?: MIMETypeValue;
  }) => void;

  /**
   * Removes a response schema from the current endpoint.
   */
  removeResponseSchema: (
    /**
     * The HTTP status code of the response
     */
    statusCode: string,
  ) => void;

  /**
   * Updates the description of a response schema.
   */
  updateResponseSchemaDescription: (args: {
    /**
     * The HTTP status code of the response
     */
    statusCode: string;
    /**
     * The id of the property to update the description for. This is a string that represents the path to the property in the schema.
     */
    id: string;
    /**
     * The description to set for the property
     */
    description: string;
    /**
     * The MIME type of the property to update the description for. Only used for response bodies.
     */
    mimeType?: MIMETypeValue;
  }) => void;

  /**
   * Updates the schema attribute of a request body, response body, or query parameter.
   */
  updateSchemaAttribute: (args: {
    /**
     * The type of the schema to update - 'request' or 'response' or a 'query' object with its location info
     */
    target: SchemaEditorTarget;
    /**
     * The id of the property to update. This is a string that represents the path to the property in the schema.
     */
    id: string;
    /**
     * The object containing the attribute to update and its value.
     */
    updateObject: UpdateSchemaAttrObject;
    /**
     * The HTTP status code of the response. Only used for response bodies.
     */
    statusCode?: string;
    /**
     * The MIME type of the property to update. Only used for response bodies.
     */
    mimeType?: MIMETypeValue;
  }) => void;
}

export interface BodySchemaEditorSlice {
  bodySchemaEditor: BodySchemaEditorSliceActions;
}

export const createBodySchemaEditorSlice: StateCreator<
  APIDesignerStore & BodySchemaEditorSlice,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  BodySchemaEditorSlice
> = (set, get) => ({
  bodySchemaEditor: {
    // unified property management functions
    addProperty: ({ target, id, statusCode, mimeType }) => {
      if (statusCode && !isStatusCode(statusCode)) {
        throw new Error(
          `Invalid status code '${statusCode}'. Status codes must be 3-digit numbers (e.g., '200', '404').`,
        );
      }

      if (!isPropertyPath(id)) {
        throw new Error(
          `Invalid property path '${id}'. Property paths must use dash-separated indices (e.g., '0', '0-1', '1-1-2').`,
        );
      }

      set(
        state => {
          if (!state.apiObject) throw new Error('apiObject is required');
          if (!state.apiObject.schema) throw new Error('schema is required');
          if (!state.apiObject.schema.paths) throw new Error('paths is required');
          if (!state.apiObject.path) throw new Error('path is required');
          if (!state.apiObject.method) throw new Error('method is required');

          const currentPath = state.apiObject.path;
          const currentMethod = state.apiObject.method;

          if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
            throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
          }

          // if the id is 0, we need to add a new property to the root object
          if (id === '0') {
            if (target === 'request') {
              if (
                state.apiObject.schema.paths[currentPath][currentMethod].requestBody &&
                'content' in state.apiObject.schema.paths[currentPath][currentMethod].requestBody
              ) {
                const existingProperties = (
                  state.apiObject.schema.paths[currentPath][currentMethod].requestBody?.content[
                    mimeType || MIMEType.ApplicationJSON
                  ]?.schema as WritableDraft<OpenAPIV3.SchemaObject>
                )?.properties;

                const currentRequestBody = state.apiObject.schema.paths[currentPath][currentMethod].requestBody;

                if (existingProperties) {
                  const newPropertyName = generateUniquePropertyName(existingProperties);

                  // Update the existing requestBody content directly to preserve all media types
                  if (currentRequestBody && 'content' in currentRequestBody && currentRequestBody.content) {
                    const currentMediaType = mimeType || MIMEType.ApplicationJSON;
                    const currentSchema = currentRequestBody.content[currentMediaType]?.schema;

                    if (currentSchema && 'properties' in currentSchema) {
                      currentSchema.properties = {
                        ...currentSchema.properties,
                        [newPropertyName]: { type: 'string' },
                      };
                    }
                  }
                } else {
                  // if the media type does not exist, we need to add it
                  (state.apiObject.schema.paths[currentPath][currentMethod]
                    .requestBody as WritableDraft<OpenAPIV3.RequestBodyObject>) = {
                    ...currentRequestBody,
                    content: {
                      ...(currentRequestBody.content as Record<string, OpenAPIV3.MediaTypeObject>),
                      [mimeType || MIMEType.ApplicationJSON]: {
                        schema: {
                          type: 'object',
                          properties: {
                            '': { type: 'string' },
                          },
                        },
                      },
                    },
                  };
                }
              } else {
                const currentRequestBody = state.apiObject.schema.paths[currentPath][currentMethod].requestBody;
                if (currentRequestBody && 'content' in currentRequestBody && currentRequestBody.content) {
                  state.apiObject.schema.paths[currentPath][currentMethod].requestBody = {
                    ...currentRequestBody,
                    content: {
                      ...currentRequestBody.content,
                      [mimeType || MIMEType.ApplicationJSON]: {
                        schema: {
                          type: 'object',
                          properties: {
                            property1: { type: 'string' },
                          },
                        },
                      },
                    },
                  };
                }
              }
            } else if (target === 'response' && statusCode) {
              if (
                state.apiObject.schema.paths[currentPath][currentMethod].responses?.[statusCode] &&
                'content' in state.apiObject.schema.paths[currentPath][currentMethod].responses[statusCode]
              ) {
                const existingContent =
                  state.apiObject.schema.paths[currentPath][currentMethod].responses[statusCode]?.content || {};

                const existingProperties =
                  (
                    state.apiObject.schema.paths[currentPath][currentMethod].responses[statusCode]?.content?.[
                      mimeType || MIMEType.ApplicationJSON
                    ]?.schema as WritableDraft<OpenAPIV3.SchemaObject>
                  )?.properties || {};

                const newPropertyName = generateUniquePropertyName(existingProperties);

                state.apiObject.schema.paths[currentPath][currentMethod].responses[statusCode] = {
                  ...state.apiObject.schema.paths[currentPath][currentMethod].responses[statusCode],
                  content: {
                    ...existingContent,
                    [mimeType || MIMEType.ApplicationJSON]: {
                      ...state.apiObject.schema.paths[currentPath][currentMethod].responses[statusCode]?.content?.[
                        mimeType || MIMEType.ApplicationJSON
                      ],
                      schema: {
                        ...state.apiObject.schema.paths[currentPath][currentMethod].responses[statusCode]?.content?.[
                          mimeType || MIMEType.ApplicationJSON
                        ]?.schema,
                        properties: {
                          ...existingProperties,
                          [newPropertyName]: { type: 'string' },
                        },
                      } as WritableDraft<OpenAPIV3.SchemaObject>,
                    },
                  } as WritableDraft<Record<string, OpenAPIV3.MediaTypeObject>>,
                };
              } else {
                state.apiObject.schema.paths[currentPath][currentMethod].responses[statusCode] = {
                  description: '',
                  content: {
                    [mimeType || MIMEType.ApplicationJSON]: {
                      schema: {
                        type: 'object',
                        properties: {
                          property1: { type: 'string' },
                        },
                      },
                    },
                  },
                };
              }
            } else if (typeof target === 'object' && target.target === 'query') {
              const parameters = (state.apiObject.schema.paths[currentPath][currentMethod].parameters || []).filter(
                param => 'in' in param && param.in === target.location,
              );
              const currentParameter = parameters[target.index];

              if (!currentParameter || !('schema' in currentParameter) || !currentParameter.schema) {
                throw new Error(`Parameter at index ${target.index} does not have a schema`);
              }
              // If the parameter is a reference, we can't update it directly
              if ('$ref' in currentParameter.schema) {
                throw new Error(`Cannot update parameter at index ${target.index} because it is a reference`);
              }

              const schema = currentParameter.schema;
              if (schema.type === 'object') {
                schema.properties = {
                  ...schema.properties,
                  '': { type: 'string' },
                };
              } else if (schema.type === 'array' && schema.items && 'properties' in schema.items) {
                schema.items.properties = {
                  ...schema.items.properties,
                  '': { type: 'string' },
                };
              } else {
                throw new Error(
                  `Cannot add property to parameter at index ${target.index} because it is not an object or array`,
                );
              }
            }
          } else if (target === 'request') {
            traverseAndModifyRequestSchema(id, state, { action: 'add' }, mimeType);
          } else if (target === 'response' && statusCode) {
            traverseAndModifyResponseSchema(statusCode, mimeType || MIMEType.ApplicationJSON, id, state, {
              action: 'add',
            });
          } else if (typeof target === 'object' && target.target === 'query') {
            traverseAndModifyQueryParam(target.location, target.index, id, state, { action: 'add' });
          }
        },
        false,
        actionLog('bodySchemaEditor.addProperty', { target, id, statusCode }),
      );
    },

    removeProperty: ({ target, id, statusCode, mimeType }) => {
      if (statusCode && !isStatusCode(statusCode)) {
        throw new Error(
          `Invalid status code '${statusCode}'. Status codes must be 3-digit numbers (e.g., '200', '404').`,
        );
      }

      if (!isPropertyPath(id)) {
        throw new Error(
          `Invalid property path '${id}'. Property paths must use dash-separated indices (e.g., '0', '0-1', '1-1-2').`,
        );
      }

      set(
        state => {
          if (!state.apiObject) throw new Error('apiObject is required');
          if (!state.apiObject.schema) throw new Error('schema is required');
          if (!state.apiObject.schema.paths) throw new Error('paths is required');
          if (!state.apiObject.path) throw new Error('path is required');
          if (!state.apiObject.method) throw new Error('method is required');

          const currentPath = state.apiObject.path;
          const currentMethod = state.apiObject.method;

          if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
            throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
          }

          // if the id is 0, we need to remove the entire body
          if (id === '0') {
            if (target === 'request') {
              const currentRequestBody = state.apiObject.schema.paths[currentPath][currentMethod].requestBody;
              if (currentRequestBody && 'content' in currentRequestBody && currentRequestBody.content) {
                if (mimeType) {
                  // Remove only the specific media type
                  delete currentRequestBody.content[mimeType];

                  // If no more media types exist, delete the entire requestBody
                  if (Object.keys(currentRequestBody.content).length === 0) {
                    delete state.apiObject.schema.paths[currentPath][currentMethod].requestBody;
                  }
                } else {
                  // No mimeType specified, delete the entire requestBody
                  delete state.apiObject.schema.paths[currentPath][currentMethod].requestBody;
                }
              } else {
                // No requestBody or no content, nothing to do
              }
            } else if (target === 'response' && statusCode) {
              if (
                state.apiObject.schema.paths[currentPath][currentMethod].responses?.[statusCode] &&
                'content' in state.apiObject.schema.paths[currentPath][currentMethod].responses[statusCode]
              ) {
                if (mimeType) {
                  delete state.apiObject.schema.paths[currentPath][currentMethod].responses[statusCode]?.content?.[
                    mimeType
                  ];

                  // Don't delete the status code even if it's the last content type
                  // The status code should remain with its description and other properties
                } else {
                  delete state.apiObject.schema.paths[currentPath][currentMethod].responses[statusCode];
                }
              }
            }
          } else if (target === 'request') {
            traverseAndModifyRequestSchema(id, state, { action: 'remove' }, mimeType);
          } else if (target === 'response' && statusCode) {
            traverseAndModifyResponseSchema(statusCode, mimeType || MIMEType.ApplicationJSON, id, state, {
              action: 'remove',
            });
          } else if (typeof target === 'object' && target.target === 'query') {
            traverseAndModifyQueryParam(target.location, target.index, id, state, { action: 'remove' });
          }
        },
        false,
        actionLog('bodySchemaEditor.removeProperty', { target, id, statusCode }),
      );
    },

    updateSchemaAttribute: ({ target, id, updateObject, statusCode, mimeType }) => {
      set(
        state => {
          if (!state.apiObject) throw new Error('apiObject is required');
          if (!state.apiObject.schema) throw new Error('schema is required');
          if (!state.apiObject.schema.paths) throw new Error('paths is required');
          if (!state.apiObject.path) throw new Error('path is required');
          if (!state.apiObject.method) throw new Error('method is required');

          const currentPath = state.apiObject.path;
          const currentMethod = state.apiObject.method;

          if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
            throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
          }

          // special case for text based response bodies
          // currently only supports text/plain and text/html
          if (
            (mimeType === MIMEType.TextPlain || mimeType === MIMEType.TextHTML) &&
            statusCode &&
            updateObject.attribute === 'example'
          ) {
            traverseAndModifyTextPlainResponseSchema(statusCode, id, state, updateObject.value as string, mimeType);
            return;
          }

          if (id === '0') {
            // this should never be hit since the root object doesn't have attributes like this (yet!)
          } else if (target === 'request') {
            traverseAndModifyRequestSchema(id, state, { action: 'update', ...updateObject }, mimeType);
          } else if (target === 'response' && statusCode) {
            traverseAndModifyResponseSchema(statusCode, mimeType || MIMEType.ApplicationJSON, id, state, {
              action: 'update',
              ...updateObject,
            });
          } else if (typeof target === 'object' && target.target === 'query') {
            traverseAndModifyQueryParam(target.location, target.index, id, state, {
              action: 'update',
              ...updateObject,
            });
          }
        },
        false,
        actionLog('bodySchemaEditor.updateSchemaAttribute', { target, updateObject, id, statusCode }),
      );
    },

    // unified initialization function
    init: (target, statusCode) => {
      const newSchema: OpenAPIV3.NonArraySchemaObject = { properties: {}, type: 'object' };
      set(
        state => {
          if (!state.apiObject) throw new Error('apiObject is required');
          if (!state.apiObject.schema) throw new Error('schema is required');
          if (!state.apiObject.schema.paths) throw new Error('paths is required');
          if (!state.apiObject.path) throw new Error('path is required');
          if (!state.apiObject.method) throw new Error('method is required');

          const currentPath = state.apiObject.path;
          const currentMethod = state.apiObject.method;

          if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
            throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
          }

          if (target === 'request') {
            state.apiObject.schema.paths[currentPath][currentMethod].requestBody = {
              content: {
                'application/json': { schema: newSchema },
              },
            };
          } else if (target === 'response' && statusCode) {
            state.apiObject.schema.paths[currentPath][currentMethod].responses[statusCode] = {
              description: '',
              content: {
                'application/json': { schema: newSchema },
              },
            };
          }
        },
        false,
        actionLog('bodySchemaEditor.initBody', { target, statusCode }),
      );
    },

    // response-specific functions (can't be easily combined)
    addResponseSchema: (statusCode, mimeType) => {
      if (statusCode && !isStatusCode(statusCode)) {
        throw new Error(
          `Invalid status code '${statusCode}'. Status codes must be 3-digit numbers (e.g., '200', '404').`,
        );
      }

      set(
        state => {
          if (!state.apiObject) throw new Error('apiObject is required');
          if (!state.apiObject.schema) throw new Error('schema is required');
          if (!state.apiObject.schema.paths) throw new Error('paths is required');
          if (!state.apiObject.path) throw new Error('path is required');
          if (!state.apiObject.method) throw new Error('method is required');

          const currentPath = state.apiObject.path;
          const currentMethod = state.apiObject.method;

          if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
            throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
          }

          const currentResponse = state.apiObject.schema.paths[currentPath][currentMethod].responses[statusCode];
          const targetMimeType = mimeType || MIMEType.ApplicationJSON;

          if (currentResponse && 'content' in currentResponse && currentResponse.content?.[targetMimeType]) {
            // we dont want to overwrite an existing schema with a new one, so we skip
            if (!currentResponse.content?.[targetMimeType]?.schema) {
              (
                state.apiObject.schema.paths[currentPath][currentMethod].responses[
                  statusCode
                ] as WritableDraft<OpenAPIV3.ResponseObject>
              ).content![targetMimeType].schema = {
                properties: {},
                type: 'object',
              };
            }
            return;
          }

          const description =
            currentResponse && 'description' in currentResponse
              ? (currentResponse.description as string)
              : codes[statusCode][0] || '';
          const content =
            currentResponse && 'content' in currentResponse
              ? (currentResponse.content as Record<string, MediaTypeObject>)
              : {};

          const newResponse = {
            description,
            content: {
              ...content,
              [targetMimeType]: {
                schema: { properties: {}, type: 'object' },
              },
            },
          };

          // add a new response schema for the given status code, defaults to empty object
          state.apiObject.schema.paths[currentPath][currentMethod].responses[statusCode] =
            newResponse as WritableDraft<OpenAPIV3.ResponseObject>;
        },
        false,
        actionLog('bodySchemaEditor.addResponseSchema', { statusCode }),
      );
    },

    removeResponseSchema: statusCode => {
      set(
        state => {
          const { apiObject } = state;
          if (!apiObject?.schema?.paths || !apiObject.path || !apiObject.method) {
            throw new Error('Required API object properties are missing');
          }

          const operation = apiObject.schema.paths[apiObject.path]?.[apiObject.method];
          if (!operation) {
            throw new Error(`Missing operation for path ${apiObject.path} and method ${apiObject.method}`);
          }

          const currentPath = apiObject.path;
          const currentMethod = apiObject.method;

          if (!apiObject.schema.paths[currentPath]?.[currentMethod]) {
            throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
          }

          delete operation.responses[statusCode];
        },
        false,
        actionLog('bodySchemaEditor.removeResponseSchema', { statusCode }),
      );
    },

    changeResponseStatusCode: (statusCode, newStatusCode) => {
      set(
        state => {
          const { apiObject } = state;
          if (!apiObject?.schema?.paths || !apiObject.path || !apiObject.method) {
            throw new Error('Required API object properties are missing');
          }

          const { path: currentPath, method: currentMethod } = apiObject;
          const operation = apiObject.schema.paths[currentPath]?.[currentMethod];
          if (!operation) {
            throw new Error(`Missing operation for path ${currentPath} and method ${currentMethod}`);
          }

          const responseData = operation.responses[statusCode];

          // Check if responseData has a description property (is a ResponseObject)
          if ('description' in responseData && responseData.description) {
            const currentDescription = responseData.description;

            // if the description is default, update it to the new status code description
            // if the description is not default, do not update it
            if (currentDescription.trim().toLowerCase() === codes[statusCode][0].trim().toLowerCase()) {
              responseData.description = codes[newStatusCode][0];
            }
          }

          if (!responseData) {
            throw new Error(`Response with status code ${statusCode} not found`);
          }

          // Move response data from old status code to new status code
          operation.responses[newStatusCode] = responseData;
          delete operation.responses[statusCode];
        },
        false,
        actionLog('bodySchemaEditor.changeResponseStatusCode', { statusCode, newStatusCode }),
      );
    },

    changeResponseMediaType: (statusCode, newMimeType, oldMimeType) => {
      if (!oldMimeType) return;

      set(state => {
        const { apiObject } = state;
        if (!apiObject?.schema?.paths || !apiObject.path || !apiObject.method) {
          throw new Error('Required API object properties are missing');
        }

        const { path: currentPath, method: currentMethod } = apiObject;
        const operation = apiObject.schema.paths[currentPath]?.[currentMethod];
        if (!operation) {
          throw new Error(`Missing operation for path ${currentPath} and method ${currentMethod}`);
        }

        const currentResponse = operation.responses[statusCode];
        if (!currentResponse) {
          throw new Error(`Response with status code ${statusCode} not found`);
        }

        const currentContent = (currentResponse as WritableDraft<OpenAPIV3.ResponseObject>).content;
        if (!currentContent) {
          throw new Error(`Content for response with status code ${statusCode} not found`);
        }

        // Move content from old MIME type to new MIME type
        currentContent[newMimeType] = currentContent[oldMimeType];
        delete currentContent[oldMimeType];
      });
    },

    getErroredResponseSchemas: () => {
      const oas = get().apiObject?.schema;
      const currentPath = get().apiObject?.path as string;
      const currentMethod = get().apiObject?.method as HttpMethods;
      const operation = oas?.paths?.[currentPath]?.[currentMethod];

      if (!operation) {
        throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
      }

      const responsesArray = Object.keys(operation?.responses || {}).map(statusCode => {
        return {
          statusCode,
          response: operation?.responses?.[statusCode] as ResponseObject,
        };
      });

      const erroredResponses = responsesArray.filter(response => {
        const responseSchemas = Object.keys(response.response.content || {});
        return responseSchemas.length > 1;
      });

      return erroredResponses.map(response => response.statusCode);
    },

    getValidResponseSchemas: () => {
      const oas = get().apiObject?.schema;
      const currentPath = get().apiObject?.path as string;
      const currentMethod = get().apiObject?.method as HttpMethods;
      const operation = oas?.paths?.[currentPath]?.[currentMethod];

      if (!operation) {
        throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
      }

      const responsesArray = Object.keys(operation?.responses || {}).map(statusCode => {
        return {
          statusCode,
          response: operation?.responses?.[statusCode] as ResponseObject,
        };
      });

      const schemas: {
        statusCode: string;
        description: string;
        schemas: {
          type: MIMETypeValue;
          schema: OpenAPIV3.SchemaObject;
        }[];
      }[] = responsesArray.map(({ statusCode, response }) => {
        const responseSchemas = Object.keys(response.content || {});

        /**
         * we should never in actuality have a schema with a ref, this filtering will be handled by the parent component
         * we also do not accept responses with no schemas
         *
         * ie. responses with only examples will still be flagged as invalid and not returned
         */
        const validResponseSchemas = responseSchemas.filter(mimeType => {
          const schema = response.content?.[mimeType]?.schema;
          if (!schema) return true;
          return !('$ref' in schema);
        });

        // temporary flag to disallow multiple schemas
        // TODO: remove this flag or set it to true once the viewer component is updated to handle multiple schemas
        const allowMultiple = false;

        if (allowMultiple) {
          const responseSchemasArray: { type: MIMETypeValue; schema: OpenAPIV3.SchemaObject }[] = [];

          validResponseSchemas.forEach(mimeType => {
            const schema = response.content?.[mimeType]?.schema;
            responseSchemasArray.push({ type: mimeType as MIMETypeValue, schema: schema as OpenAPIV3.SchemaObject });
          });

          return {
            statusCode,
            description: response.description,
            schemas: responseSchemasArray,
          };
        }

        const preferredMimeType = validResponseSchemas.includes('application/json')
          ? 'application/json'
          : validResponseSchemas[0];
        const responseSchemasArray: { type: MIMETypeValue; schema: OpenAPIV3.SchemaObject }[] = [];

        if (preferredMimeType) {
          const schema = response.content?.[preferredMimeType]?.schema;
          responseSchemasArray.push({
            type: preferredMimeType as MIMETypeValue,
            schema: schema as OpenAPIV3.SchemaObject,
          });
        }

        // do not create stub schemas if there are no schemas, simply return an empty array
        const finalSchemas = responseSchemasArray.length > 0 ? responseSchemasArray : [];

        return {
          statusCode,
          description: response.description,
          schemas: finalSchemas,
        };
      });

      return schemas;
    },

    updateResponseSchemaDescription: ({ statusCode, id, description, mimeType }) => {
      set(
        state => {
          if (!state.apiObject) throw new Error('apiObject is required');
          if (!state.apiObject.schema) throw new Error('schema is required');
          if (!state.apiObject.schema.paths) throw new Error('paths is required');
          if (!state.apiObject.path) throw new Error('path is required');
          if (!state.apiObject.method) throw new Error('method is required');

          const currentPath = state.apiObject.path;
          const currentMethod = state.apiObject.method;

          if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
            throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
          }

          // get the response data for the status code
          const response = state.apiObject.schema.paths[currentPath][currentMethod].responses[statusCode];

          if (!response) {
            throw new Error(`Response with status code ${statusCode} not found`);
          }

          if (id === '0') {
            // update the response description for the root level
            if ('description' in response) {
              (response as WritableDraft<OpenAPIV3.ResponseObject>).description = description;
            }
          } else {
            // update description for nested schema properties
            traverseAndModifyResponseSchema(statusCode, mimeType || MIMEType.ApplicationJSON, id, state, {
              action: 'update',
              attribute: 'description',
              value: description,
            });
          }
        },
        false,
        actionLog('bodySchemaEditor.updateResponseSchemaDescription', { statusCode, id, description, mimeType }),
      );
    },

    getAllResponseStatusCodes: () => {
      const oas = get().apiObject?.schema;
      const currentPath = get().apiObject?.path as string;
      const currentMethod = get().apiObject?.method as HttpMethods;
      const operation = oas?.paths?.[currentPath]?.[currentMethod];

      if (!operation) {
        throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
      }

      return Object.keys(operation.responses || {});
    },

    getExistingMediaTypes: (statusCode: string) => {
      const oas = get().apiObject?.schema;
      const currentPath = get().apiObject?.path as string;
      const currentMethod = get().apiObject?.method as HttpMethods;
      const operation = oas?.paths?.[currentPath]?.[currentMethod];

      if (!operation) {
        throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
      }

      const response = operation.responses?.[statusCode] as ResponseObject;
      if (!response) {
        throw new Error(`Response with status code ${statusCode} not found`);
      }

      return Object.keys(response.content || {}).map(mimeType => mimeType as MIMETypeValue);
    },
  },
});
